import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Activity, ChevronRight, CircleAlert, CircleCheck, Cog, Cpu, Download, FileCheck, FlaskConical, HardDrive, Inbox, Loader2, Package, RefreshCw, RotateCcw, ServerCog, ShieldCheck } from 'lucide-react';
import { useSiteTheme } from '../hooks/useSiteTheme';
import { ENGINE_EVENT_KIND, ENGINE_STATE, getEngineReport, reinstallEngine, runEngineSelfTest } from '../lib/adminApi';
import { apiErrorMessage } from '../lib/projectApi';
import { SiteHeader } from '../components/billing/SiteHeader';
import { SiteFooter } from '../components/legal/SiteFooter';
import { OwnerNav } from '../components/owner/OwnerNav';
import { EngineEventDialog, TONE_BADGE, eventResult } from '../components/owner/EngineEventDialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const heading = { fontFamily: 'Outfit, Inter, sans-serif' };

const when = (iso) => {
  if (!iso) return '';
  const d = typeof iso === 'number' ? new Date(iso * 1000) : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.round(diff / 60000)} min ago`;
  if (diff < 86400000) return `${Math.round(diff / 3600000)} h ago`;
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const exact = (iso) => { const d = typeof iso === 'number' ? new Date(iso * 1000) : new Date(iso); return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(); };
const mb = (bytes) => (bytes == null ? '—' : bytes >= 1073741824 ? `${(bytes / 1073741824).toFixed(1)} GB` : `${Math.round(bytes / 1048576)} MB`);
const ms = (v) => (v == null ? '' : v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${v} ms`);

/* ------------------------------------------------------------------ */
/* Facts grid                                                            */
/* ------------------------------------------------------------------ */

const Fact = ({ icon: Icon, label, value, detail, testId, mono }) => (
  <div className="rounded-lg border border-border bg-background/60 p-3 min-w-0" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground"><Icon className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" /><span className="truncate">{label}</span></div>
    <div className={`mt-1 text-sm font-medium break-words ${mono ? 'font-mono text-[12px]' : ''}`} data-testid={`${testId}-value`}>{value ?? '—'}</div>
    {detail && <div className="mt-0.5 text-[11px] text-muted-foreground leading-snug break-words" data-testid={`${testId}-detail`}>{detail}</div>}
  </div>
);

/* ------------------------------------------------------------------ */
/* Status card                                                           */
/* ------------------------------------------------------------------ */

const StatusCard = ({ engine, pdftoppm, onSelfTest, testing, onReinstall, reinstalling }) => {
  const st = ENGINE_STATE[engine.state] || ENGINE_STATE.unknown;
  const installing = engine.state === 'installing';
  const pct = engine.progress != null ? Math.round(engine.progress * 100) : null;
  const host = engine.host || {};
  const source = engine.source === 'system' ? `The host's LibreOffice (${engine.soffice || 'on PATH'})` : engine.source === 'runtime' ? 'The app\u2019s own self-contained runtime' : installing ? (engine.available ? `The host's LibreOffice (${engine.soffice || 'on PATH'}) keeps serving imports while the app\u2019s own runtime is being prepared` : 'The app\u2019s own runtime is being prepared') : 'No engine available';
  return (
    <section className="rounded-xl border border-border bg-card" data-testid="engine-status-card">
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4 border-b border-border">
        <div className="flex items-start gap-3 min-w-0">
          <span className={`mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${engine.available ? 'bg-[hsl(var(--wp-context-green))]/15 text-[hsl(var(--wp-context-green))]' : installing ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'}`}>
            {installing ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : engine.available ? <CircleCheck className="h-5 w-5" aria-hidden="true" /> : <CircleAlert className="h-5 w-5" aria-hidden="true" />}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold leading-tight" style={heading}>Publisher conversion engine</h2>
              <Badge className={`${TONE_BADGE[st.tone]} whitespace-nowrap`} data-testid="engine-state-badge">{st.label}</Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground" data-testid="engine-source">{source}</p>
            {engine.version && <p className="mt-0.5 text-xs text-muted-foreground" data-testid="engine-version">{engine.version}{engine.self_contained ? ' · self-contained' : ''}</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" className="h-9" onClick={onSelfTest} disabled={testing || !engine.available} data-testid="engine-selftest-button">
            {testing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden="true" /> : <FlaskConical className="h-4 w-4 mr-1.5" aria-hidden="true" />}Run self-test
          </Button>
          <Button type="button" variant="outline" className="h-9" onClick={onReinstall} disabled={reinstalling || installing} data-testid="engine-reinstall-button">
            {reinstalling ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden="true" /> : <RotateCcw className="h-4 w-4 mr-1.5" aria-hidden="true" />}Reinstall runtime
          </Button>
        </div>
      </div>

      {installing && (
        <div className="px-4 py-3 border-b border-border" data-testid="engine-progress" aria-live="polite">
          <div className="flex items-center justify-between gap-3 text-xs">
            <span className="text-foreground">{engine.message || 'Preparing the conversion engine…'}</span>
            <span className="tabular-nums text-muted-foreground">{pct != null ? `${pct}%` : engine.step || ''}</span>
          </div>
          <Progress value={pct ?? 0} className="mt-2 h-1.5" aria-label="Download progress" />
          {engine.total_packages ? <div className="mt-1 text-[11px] text-muted-foreground">{engine.done_packages ?? 0} of {engine.total_packages} packages · {mb(engine.done_bytes)} of {mb(engine.total_bytes)}</div> : null}
        </div>
      )}

      {engine.state === 'failed' && engine.error && (
        <div className="px-4 py-3 border-b border-border text-xs text-destructive flex items-start gap-1.5" role="alert" data-testid="engine-install-error"><CircleAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" /><span className="break-words">{engine.error}</span></div>
      )}

      <div className="grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="engine-facts">
        <Fact icon={Cpu} label="Server" value={host.os || '—'} detail={[host.libc, host.machine].filter(Boolean).join(' · ')} testId="engine-fact-host" />
        <Fact icon={ServerCog} label="Engine in use" value={engine.source === 'system' ? 'Host LibreOffice' : engine.source === 'runtime' ? 'App runtime' : installing ? (engine.available ? 'Host LibreOffice' : 'Preparing…') : 'None'} detail={engine.soffice || undefined} testId="engine-fact-source" />
        <Fact icon={Package} label="App runtime on disk" value={engine.runtime_present ? `${mb(engine.runtime_bytes)} · ${engine.installed_packages ?? '?'} packages` : 'Not installed'} detail={engine.installed_at ? `Installed ${when(engine.installed_at)}${engine.installed_host?.machine ? ` on ${engine.installed_host.machine}` : ''}` : engine.prefix} testId="engine-fact-runtime" />
        <Fact icon={HardDrive} label="Disk" value={engine.disk?.free_bytes != null ? `${mb(engine.disk.free_bytes)} free` : '—'} detail={engine.disk?.total_bytes != null ? `of ${mb(engine.disk.total_bytes)} at ${engine.prefix}` : engine.prefix} testId="engine-fact-disk" />
        <Fact icon={Download} label="Pinned package set" value={engine.manifest?.packages != null ? `${engine.manifest.packages} packages · ${engine.manifest.download_mb ?? '?'} MB` : engine.manifest?.error || '—'} detail={engine.manifest?.suite ? `${engine.manifest.suite}${engine.manifest.requires_glibc ? ` · needs glibc ≥ ${engine.manifest.requires_glibc}` : ''}` : undefined} testId="engine-fact-manifest" />
        <Fact icon={ShieldCheck} label="Settings" value={engine.settings ? `${engine.settings.auto_install ? 'Auto-install on' : 'Auto-install off'}${engine.settings.force_runtime ? ' · runtime forced' : ''}` : '—'} detail={[pdftoppm ? 'pdftoppm available' : 'pdftoppm missing (PDF previews use PyMuPDF)', engine.settings?.use_host_packages ? 'uses host packages (not recommended)' : null, engine.settings?.soffice_path_override ? `SOFFICE_PATH = ${engine.settings.soffice_path_override}` : null].filter(Boolean).join(' · ')} testId="engine-fact-settings" />
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Last error + recent activity                                          */
/* ------------------------------------------------------------------ */

const LastError = ({ error, onView }) => (
  <section className={`rounded-xl border p-4 ${error ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-card'}`} data-testid="engine-last-error">
    <div className="flex items-center gap-2">
      <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${error ? 'bg-destructive/15 text-destructive' : 'bg-[hsl(var(--wp-context-green))]/15 text-[hsl(var(--wp-context-green))]'}`}>{error ? <CircleAlert className="h-4 w-4" aria-hidden="true" /> : <CircleCheck className="h-4 w-4" aria-hidden="true" />}</span>
      <div className="min-w-0">
        <h2 className="text-base font-semibold leading-tight" style={heading}>Last error</h2>
        <p className="text-[11px] text-muted-foreground">{error ? `${ENGINE_EVENT_KIND[error.kind] || error.kind} · ${when(error.at)}` : 'Nothing has gone wrong in the last 30 days.'}</p>
      </div>
    </div>
    {error && (
      <div className="mt-3 space-y-1 text-xs">
        {error.file && <div className="text-muted-foreground">File: <span className="text-foreground" data-testid="engine-last-error-file">{error.file}</span></div>}
        {error.code && <div className="text-muted-foreground">Code: <span className="font-mono text-foreground" data-testid="engine-last-error-code">{error.code}</span></div>}
        <p className="text-foreground break-words" data-testid="engine-last-error-message">{error.message}</p>
        <p className="text-[11px] text-muted-foreground" title={exact(error.at)}>{exact(error.at)}</p>
        <Button type="button" size="sm" variant="outline" className="h-8 mt-1" onClick={() => onView(error)} data-testid="engine-last-error-details">
          <FileCheck className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />{error.has_log ? 'View details & log' : 'View details'}
        </Button>
      </div>
    )}
  </section>
);

const Activity7d = ({ counts }) => (
  <section className="rounded-xl border border-border bg-card p-4" data-testid="engine-activity-7d">
    <div className="flex items-center gap-2">
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Activity className="h-4 w-4" aria-hidden="true" /></span>
      <div><h2 className="text-base font-semibold leading-tight" style={heading}>Last 7 days</h2><p className="text-[11px] text-muted-foreground">Imports, PDF / PNG exports and self-tests.</p></div>
    </div>
    <div className="mt-3 grid grid-cols-2 gap-3">
      <div><div className="text-2xl font-semibold tabular-nums" style={heading} data-testid="engine-7d-conversions">{counts?.conversions ?? 0}</div><div className="text-[11px] text-muted-foreground">conversions</div></div>
      <div><div className={`text-2xl font-semibold tabular-nums ${counts?.failed ? 'text-destructive' : ''}`} style={heading} data-testid="engine-7d-failed">{counts?.failed ?? 0}</div><div className="text-[11px] text-muted-foreground">engine failures</div></div>
    </div>
  </section>
);

const Events = ({ rows, loading, onSelect }) => (
  <section className="rounded-xl border border-border bg-card" data-testid="engine-events">
    <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><FileCheck className="h-4 w-4" aria-hidden="true" /></span>
      <div><h2 className="text-base font-semibold leading-tight" style={heading}>Recent activity</h2><p className="text-[11px] text-muted-foreground">The last 25 conversions, self-tests and runtime installs on this server. Click a row for the details and the LibreOffice log.</p></div>
    </div>
    {loading ? (
      <div className="p-4 space-y-2" aria-busy="true"><Skeleton className="h-7 w-full" /><Skeleton className="h-7 w-11/12" /><Skeleton className="h-7 w-10/12" /></div>
    ) : rows.length === 0 ? (
      <div className="p-6 text-center text-sm text-muted-foreground flex flex-col items-center gap-2" data-testid="engine-events-empty"><Inbox className="h-6 w-6 text-primary" aria-hidden="true" />No activity yet. Run a self-test or import a Publisher file and it will show up here.</div>
    ) : (
      <Table className="text-xs table-fixed w-full">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-9 w-[84px] sm:w-[96px] whitespace-nowrap">When</TableHead>
            <TableHead className="h-9 w-[90px] sm:w-[110px]">What</TableHead>
            <TableHead className="h-9">File / details</TableHead>
            <TableHead className="h-9 w-[64px] text-right hidden sm:table-cell">Took</TableHead>
            <TableHead className="h-9 w-[104px] sm:w-[120px] text-right">Result</TableHead>
            <TableHead className="h-9 w-[28px]"><span className="sr-only">Details</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const res = eventResult(r);
            const detail = r.file || r.message || '';
            const open = () => onSelect(r);
            return (
              <TableRow
                key={r.id}
                role="button"
                tabIndex={0}
                aria-label={`${ENGINE_EVENT_KIND[r.kind] || r.kind}${r.file ? ` ${r.file}` : ''} — ${res.label}. Open details`}
                onClick={open}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
                className={`cursor-pointer focus-visible:outline-none focus-visible:bg-muted/70 ${!r.ok ? 'bg-destructive/5 hover:bg-destructive/10' : ''}`}
                data-testid="engine-event-row"
                data-event-id={r.id}
                data-result={r.result || (r.ok ? 'ok' : 'failed')}
              >
                <TableCell className="py-2 truncate text-muted-foreground" title={exact(r.at)}>{when(r.at)}</TableCell>
                <TableCell className="py-2 truncate">{ENGINE_EVENT_KIND[r.kind] || r.kind}</TableCell>
                <TableCell className="py-2 min-w-0" title={[r.file, r.message].filter(Boolean).join(' — ')}><span className="block truncate" data-testid="engine-event-detail">{detail}{r.file && r.message && !r.ok ? <span className="text-destructive"> — {r.message}</span> : null}</span></TableCell>
                <TableCell className="py-2 text-right tabular-nums text-muted-foreground hidden sm:table-cell">{ms(r.duration_ms)}</TableCell>
                <TableCell className="py-2 text-right"><Badge className={`${TONE_BADGE[res.tone]} whitespace-nowrap`} title={r.message || ''} data-testid="engine-event-result">{res.label}</Badge></TableCell>
                <TableCell className="py-2 px-1 text-muted-foreground"><ChevronRight className="h-3.5 w-3.5" aria-hidden="true" /></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    )}
  </section>
);

/* ------------------------------------------------------------------ */
/* Page                                                                  */
/* ------------------------------------------------------------------ */

export default function OwnerEngine() {
  const [theme, toggleTheme] = useSiteTheme();
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [lastTest, setLastTest] = useState(null);
  const [confirmReinstall, setConfirmReinstall] = useState(false);
  const [reinstalling, setReinstalling] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const alive = useRef(true);

  useEffect(() => { document.title = 'Conversion engine · Owner · WannaPublish'; alive.current = true; return () => { alive.current = false; }; }, []);

  const load = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try { const r = await getEngineReport(); if (alive.current) { setReport(r); setError(''); } } catch (e) { if (alive.current) setError(await apiErrorMessage(e, 'The engine status could not be loaded.')); } finally { if (alive.current) setRefreshing(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // follow an install closely, otherwise refresh every half minute
  const installing = report?.engine?.state === 'installing';
  useEffect(() => {
    const t = setInterval(load, installing ? 3000 : 30000);
    return () => clearInterval(t);
  }, [load, installing]);

  const selfTest = async () => {
    setTesting(true);
    try {
      const r = await runEngineSelfTest();
      setLastTest(r);
      if (r.ok) toast.success('Self-test passed', { description: `${r.message} in ${ms(r.duration_ms)}.` });
      else toast.error('Self-test failed', { description: r.message });
    } catch (e) {
      toast.error('Self-test could not run', { description: await apiErrorMessage(e, 'Please try again.') });
    } finally { setTesting(false); load(); }
  };

  const reinstall = async () => {
    setConfirmReinstall(false);
    setReinstalling(true);
    try {
      const r = await reinstallEngine();
      toast.success(r.started ? 'Reinstall started' : 'An install is already running', { description: r.started ? 'The runtime is being downloaded again in the background — this page follows the progress.' : 'Progress is shown above.' });
    } catch (e) {
      toast.error('Reinstall could not start', { description: await apiErrorMessage(e, 'Please try again.') });
    } finally { setReinstalling(false); load(); }
  };

  const engine = report?.engine;

  return (
    <main className="min-h-[100dvh] flex flex-col bg-background text-foreground" data-testid="owner-engine-page" data-theme={theme}>
      <SiteHeader theme={theme} onToggleTheme={toggleTheme} testId="owner-header" />
      <section className="flex-1 w-full max-w-[1180px] mx-auto px-4 sm:px-6 pb-12 pt-2 sm:pt-4">
        <OwnerNav />
        <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Cog className="h-5 w-5" aria-hidden="true" /></span>
            <div className="min-w-0">
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={heading} data-testid="owner-engine-title">Conversion engine</h1>
              <p className="mt-1 text-sm text-muted-foreground">Is Publisher import working on this server? State, version, the last error and what the engine has been doing — no API calls needed.</p>
            </div>
          </div>
          <Button type="button" variant="outline" className="h-10" onClick={() => load(true)} disabled={refreshing} data-testid="owner-engine-refresh"><RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />Refresh</Button>
        </div>

        {error && <p className="mt-4 text-xs text-destructive flex items-center gap-1.5" role="alert" data-testid="owner-engine-error"><CircleAlert className="h-3.5 w-3.5" aria-hidden="true" />{error}</p>}

        <div className="mt-6">
          {engine ? (
            <StatusCard engine={engine} pdftoppm={report.pdftoppm} onSelfTest={selfTest} testing={testing} onReinstall={() => setConfirmReinstall(true)} reinstalling={reinstalling} />
          ) : (
            <Skeleton className="h-64 w-full rounded-xl" />
          )}
        </div>

        {lastTest && (
          <div className={`mt-3 rounded-xl border px-4 py-3 text-sm flex items-start gap-2 ${lastTest.ok ? 'border-[hsl(var(--wp-context-green))]/40 bg-[hsl(var(--wp-context-green))]/5' : 'border-destructive/40 bg-destructive/5'}`} role="status" data-testid="engine-selftest-result">
            {lastTest.ok ? <CircleCheck className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--wp-context-green))]" aria-hidden="true" /> : <CircleAlert className="h-4 w-4 mt-0.5 shrink-0 text-destructive" aria-hidden="true" />}
            <div className="min-w-0">
              <div className="font-medium">{lastTest.ok ? 'Self-test passed' : 'Self-test failed'}<span className="ml-2 text-xs font-normal text-muted-foreground tabular-nums">{ms(lastTest.duration_ms)}</span></div>
              <div className="text-xs text-muted-foreground break-words">{lastTest.message}{lastTest.code ? ` (${lastTest.code})` : ''}</div>
            </div>
          </div>
        )}

        <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] items-start">
          <div className="min-w-0">
            <Events rows={report?.events || []} loading={!report} onSelect={setSelectedEvent} />
          </div>
          <div className="space-y-4 min-w-0">
            {report ? <LastError error={report.last_error} onView={setSelectedEvent} /> : <Skeleton className="h-24 w-full rounded-xl" />}
            {report ? <Activity7d counts={report.counts_7d} /> : <Skeleton className="h-24 w-full rounded-xl" />}
          </div>
        </div>
      </section>
      <SiteFooter testId="owner-footer" />

      <EngineEventDialog event={selectedEvent} onClose={() => setSelectedEvent(null)} />

      <AlertDialog open={confirmReinstall} onOpenChange={setConfirmReinstall}>
        <AlertDialogContent data-testid="engine-reinstall-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle style={heading}>Reinstall the app's own runtime?</AlertDialogTitle>
            <AlertDialogDescription>
              The current copy ({engine?.runtime_present ? mb(engine.runtime_bytes) : 'none'}) is deleted and the pinned package set ({engine?.manifest?.download_mb ?? '~130'} MB) is downloaded again in the background. Imports keep working through the host's LibreOffice if there is one; otherwise they show "preparing" for a couple of minutes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="engine-reinstall-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={reinstall} data-testid="engine-reinstall-confirm">Reinstall</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
